import { useState } from "react";

import type {
  KnowledgePageDto,
  KnowledgePageStatus,
  KnowledgeTopicDto,
} from "@oracus/shared/api/knowledge";

import {
  useCreateTopic,
  useDeleteTopic,
  useGenerateTopic,
  useKnowledgeTopics,
  usePageDetail,
  usePublishPage,
  useRejectPage,
  useTopicPages,
} from "../api/knowledge.api.ts";
import { ApiError } from "../api/client.ts";
import { MarkdownAnswer } from "../components/MarkdownAnswer.tsx";
import { Badge, Button, Card, Page } from "../components/Page.tsx";

const STATUS_TONE: Record<
  KnowledgePageStatus,
  "neutral" | "info" | "success" | "warn" | "danger"
> = {
  generating: "neutral",
  draft: "info",
  published: "success",
  stale: "warn",
  rejected: "danger",
};

export function Knowledge() {
  const topics = useKnowledgeTopics();
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  return (
    <Page
      title="Manage knowledge"
      description="Curate the topics the hub should cover. Oracus gathers code + Jira evidence per topic and drafts a grounded entry; you review and publish."
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,360px)_1fr]">
        <div className="space-y-6">
          <CreateTopicCard />
          <Card title="Topics">
            {topics.isLoading && (
              <p className="text-sm text-zinc-500">Loading…</p>
            )}
            {topics.data && topics.data.topics.length === 0 && (
              <p className="text-sm text-zinc-600">No topics yet.</p>
            )}
            <ul className="space-y-2">
              {topics.data?.topics.map((t) => (
                <TopicRow
                  key={t.id}
                  topic={t}
                  selected={t.id === selectedTopicId}
                  onSelect={() => {
                    setSelectedTopicId(t.id);
                    setSelectedPageId(null);
                  }}
                />
              ))}
            </ul>
          </Card>
        </div>

        <div className="space-y-6">
          {selectedTopicId ? (
            <RevisionsPanel
              topicId={selectedTopicId}
              selectedPageId={selectedPageId}
              onSelectPage={setSelectedPageId}
            />
          ) : (
            <Card>
              <p className="text-sm text-zinc-600">
                Select a topic to see its drafts and published revision.
              </p>
            </Card>
          )}
          {selectedPageId && <ReviewPanel pageId={selectedPageId} />}
        </div>
      </div>
    </Page>
  );
}

function CreateTopicCard() {
  const create = useCreateTopic();
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const canSubmit =
    title.trim().length > 0 && prompt.trim().length > 0 && !create.isPending;

  return (
    <Card title="New topic">
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-zinc-700">Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="How checkout works"
            className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-zinc-700">What should this entry cover?</span>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Explain the end-to-end checkout flow, current known issues, and recent changes — for a non-technical reader."
            className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
          />
        </label>
        <Button
          disabled={!canSubmit}
          onClick={() => {
            create.mutate(
              { title: title.trim(), prompt: prompt.trim() },
              {
                onSuccess: () => {
                  setTitle("");
                  setPrompt("");
                },
              },
            );
          }}
        >
          {create.isPending ? "Creating…" : "Create topic"}
        </Button>
        {create.isError && (
          <p className="text-sm text-red-600">
            {errorMessage(create.error)}
          </p>
        )}
      </div>
    </Card>
  );
}

function TopicRow({
  topic,
  selected,
  onSelect,
}: {
  topic: KnowledgeTopicDto;
  selected: boolean;
  onSelect: () => void;
}) {
  const generate = useGenerateTopic();
  const del = useDeleteTopic();

  return (
    <li
      className={`rounded-md border p-3 ${
        selected ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"
      }`}
    >
      <button onClick={onSelect} className="block w-full text-left">
        <span className="block text-sm font-medium text-zinc-900">
          {topic.title}
        </span>
        <p className="mt-1 line-clamp-2 text-xs text-zinc-500">
          {topic.prompt}
        </p>
      </button>
      <div className="mt-2 flex items-center gap-2">
        <Button
          variant="secondary"
          className="!px-2.5 !py-1 text-xs"
          disabled={generate.isPending}
          onClick={() =>
            generate.mutate(topic.id, {
              // Open the topic so its Revisions panel mounts and starts
              // polling — the new draft shows up there when generation
              // finishes (typically 10–40s).
              onSuccess: () => onSelect(),
            })
          }
        >
          {generate.isPending ? "Queuing…" : "Generate draft"}
        </Button>
        <Button
          variant="danger"
          className="!px-2.5 !py-1 text-xs"
          disabled={del.isPending}
          onClick={() => {
            if (confirm(`Delete topic "${topic.title}"? This removes its drafts.`))
              del.mutate(topic.id);
          }}
        >
          Delete
        </Button>
        <span className="ml-auto">
          {topic.publishedPageId ? (
            <Badge tone="success">Live</Badge>
          ) : (
            <Badge tone="neutral">No live entry</Badge>
          )}
        </span>
      </div>
      {generate.isError && (
        <p className="mt-2 text-xs text-red-600">
          {errorMessage(generate.error)}
        </p>
      )}
      {generate.isSuccess &&
        (generate.data?.enqueued ? (
          <p className="mt-2 text-xs text-zinc-500">
            Draft queued — generating in the background. It’ll appear under
            Revisions in a few seconds. (Requires the worker to be running.)
          </p>
        ) : (
          <p className="mt-2 text-xs text-amber-600">
            Queue unavailable — generation was not enqueued.
          </p>
        ))}
    </li>
  );
}

function RevisionsPanel({
  topicId,
  selectedPageId,
  onSelectPage,
}: {
  topicId: string;
  selectedPageId: string | null;
  onSelectPage: (id: string) => void;
}) {
  const pages = useTopicPages(topicId);

  return (
    <Card title="Revisions">
      {pages.isLoading && <p className="text-sm text-zinc-500">Loading…</p>}
      {pages.data && pages.data.pages.length === 0 && (
        <p className="text-sm text-zinc-600">
          No revisions yet. Use “Generate draft”. A new draft appears here once
          generation finishes.
        </p>
      )}
      <ul className="space-y-2">
        {pages.data?.pages.map((p) => (
          <li key={p.id}>
            <button
              onClick={() => onSelectPage(p.id)}
              className={`flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left ${
                p.id === selectedPageId
                  ? "border-zinc-900 bg-zinc-50"
                  : "border-zinc-200"
              }`}
            >
              <span className="text-sm text-zinc-800">
                v{p.version} · {p.title}
              </span>
              <Badge tone={STATUS_TONE[p.status]}>{p.status}</Badge>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ReviewPanel({ pageId }: { pageId: string }) {
  const detail = usePageDetail(pageId);
  const publish = usePublishPage();
  const reject = useRejectPage();

  if (detail.isLoading || !detail.data) {
    return (
      <Card>
        <p className="text-sm text-zinc-500">Loading draft…</p>
      </Card>
    );
  }

  const { page, publishedPage } = detail.data;
  const unsupported =
    page.coverage?.entries.filter((e) => !e.supported) ?? [];
  const blocked = page.coverage ? !page.coverage.allSupported : false;
  const terminal = page.status === "rejected";

  return (
    <Card
      title={`Review · v${page.version}`}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={blocked || terminal || publish.isPending}
            onClick={() => publish.mutate(page.id)}
          >
            {publish.isPending ? "Publishing…" : "Approve & publish"}
          </Button>
          <Button
            variant="secondary"
            disabled={terminal || reject.isPending}
            onClick={() => reject.mutate(page.id)}
          >
            Reject
          </Button>
          {blocked && (
            <span className="text-xs text-amber-600">
              Has unverified sections — publishing is blocked.
            </span>
          )}
          {publish.isError && (
            <span className="text-xs text-red-600">
              {errorMessage(publish.error)}
            </span>
          )}
        </div>
      }
    >
      <div className="mb-3 flex items-center gap-2">
        <Badge tone={STATUS_TONE[page.status]}>{page.status}</Badge>
        {publishedPage && (
          <span className="text-xs text-zinc-500">
            Currently live: v{publishedPage.version}
          </span>
        )}
      </div>

      {unsupported.length > 0 && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-800">
            Unverified sections (not grounded in cited sources):
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-amber-800">
            {unsupported.map((e, i) => (
              <li key={i}>
                <span className="font-medium">{e.section}</span>
                {e.note ? ` — ${e.note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-md border border-zinc-100 bg-white p-4">
        <MarkdownAnswer content={page.body} className="max-w-3xl" />
      </div>

      {page.citations.length > 0 && (
        <div className="mt-4 border-t border-zinc-100 pt-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
            Sources ({page.citations.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {page.citations.map((c) => (
              <Badge key={`${c.sourceType}:${c.sourceId}`} tone="info">
                {c.label}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | undefined;
    return body?.message ?? err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
